import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
    EmployeeAssignmentDto,
    MatterAssignmentDto,
    TaskAssignmentDto,
} from '../models/request.models';

/**
 * Service for assignments that are anchored on a matter (and optionally a Request).
 * Covers both Request-linked assignments and direct assignments created without a Request.
 */
@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/assignments`;

  /**
   * Create a direct assignment (no Request workflow). The new row is PRIMARY, and PENDING
   * unless `status` names another ASSIGNED_RESOURCE_STATUS code. Left out, the API applies
   * its own default rather than this client choosing one.
   */
  createDirectAssignment(
    clientMatterNumber: string,
    employeeId: string,
    startDate?: string | null,
    endDate?: string | null,
    status?: string | null,
  ): Observable<MatterAssignmentDto> {
    return this.http.post<MatterAssignmentDto>(this.apiUrl, {
      clientMatterNumber,
      employeeId,
      ...(startDate != null && { startDate }),
      ...(endDate != null && { endDate }),
      ...(status != null && { status }),
    });
  }

  /**
   * Soft delete an assignment (Request-linked or direct).
   */
  deleteAssignment(assignmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${assignmentId}`);
  }

  /**
   * Update an assignment's status (ACTIVE / PENDING / INACTIVE / CLOSED).
   */
  updateAssignmentStatus(
    assignmentId: number,
    status: string,
  ): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${assignmentId}/status`, {
      status,
    });
  }

  /**
   * Update an assignment's start and/or end date. Pass null to clear.
   */
  updateAssignmentDates(
    assignmentId: number,
    startDate: string | null,
    endDate: string | null,
  ): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${assignmentId}/dates`, {
      startDate,
      endDate,
    });
  }

  /**
   * Update an assignment's type (PRIMARY / COVERAGE) by assignment id.
   */
  updateAssignmentType(
    assignmentId: number,
    assignmentTypeCategory: string,
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.apiUrl}/${assignmentId}/assignment-type`,
      { assignmentTypeCategory },
    );
  }

  /**
   * Assign matter tasks to an existing assignment, in ONE call per user action.
   *
   * The API sends a single notification per call, however many codes it carries, which is
   * what makes "several tasks assigned in one action produce one notification" hold. Callers
   * must therefore never loop this per task — pass every selected code in `taskCodes`.
   *
   * Returns only the rows actually created: codes already actively assigned are skipped
   * server-side, so an all-duplicate call resolves to an empty array and sends no
   * notification. Errors: 404 when the assignment is gone, 400 when the list is empty or a
   * code is not on the matter's available tasks, and 503 when the matter's task list could not
   * be read at all — the matter number comes off the stored assignment, not off this request,
   * so that failure is ours and a retry may help. The 400 is what a create hits whenever the
   * matter offers no tasks, since then no submitted code can validate.
   */
  createTaskAssignments(
    assignmentId: number,
    taskCodes: string[],
  ): Observable<TaskAssignmentDto[]> {
    return this.http.post<TaskAssignmentDto[]>(
      `${this.apiUrl}/${assignmentId}/tasks`,
      { taskCodes },
    );
  }

  /**
   * List all assignments for a matter (Request-linked and direct/decoupled).
   */
  getAssignmentsByMatter(
    clientMatterNumber: string,
  ): Observable<MatterAssignmentDto[]> {
    return this.http.get<MatterAssignmentDto[]>(
      `${this.apiUrl}/by-matter/${encodeURIComponent(clientMatterNumber)}`,
    );
  }

  /**
   * List all assignments for an employee (Request-linked and direct/decoupled).
   */
  getAssignmentsByEmployee(
    employeeId: string,
  ): Observable<EmployeeAssignmentDto[]> {
    return this.http.get<EmployeeAssignmentDto[]>(
      `${this.apiUrl}/by-employee/${encodeURIComponent(employeeId)}`,
    );
  }

  /**
   * List the current user's own assignments (self-service).
   * Manager notes are stripped server-side when the caller lacks
   * Assignments.ManagerNotes.Read. The caller's own resource notes are always returned.
   */
  getMyAssignments(): Observable<EmployeeAssignmentDto[]> {
    return this.http.get<EmployeeAssignmentDto[]>(`${this.apiUrl}/my`);
  }
}
